import type { Page } from '../../app/feature';
import type { DrawConfig } from '../../shared/types';
import { q, qa } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { commit, getState } from '../../shared/store/store';
import { toast } from '../../shared/ui/toast';

/**
 * Ajustes do sorteio e do estúdio ao vivo. Cada ajuste é uma linha com título, explicação e o
 * controle à direita; a prévia ao lado mostra como o número aparece com a configuração atual.
 */
export const settings: Page = {
  id: 'config',
  pattern: '/ajustes',
  roles: ['ADMIN'],
  nav: { key: 'nav.config', icon: '⚙️', group: 'config' },

  template: () => `
<div>
  <div class="view-head">
    <div>
      <h2 data-i18n="cfg.title">Ajustes</h2>
      <p data-i18n="cfg.sub3">Cómo se sortea y cómo se ve la revelación en la live. Cada cambio se guarda al instante y vale para la próxima transmisión; los resultados ya publicados no cambian.</p>
    </div>
  </div>

  <div class="cols">
    <div class="span-7 stack">

      <section class="panel">
        <div class="panel-head"><h3 data-i18n="cfg.draw">Reglas del sorteo</h3></div>
        <div class="settings">
          <label class="setting">
            <div class="setting-txt">
              <b data-i18n="cfg.oneperT">Un premio por jugador</b>
              <span data-i18n="cfg.oneperD">Si un jugador ya ganó en esta live, sus otros números salen del bombo hasta el final del sorteo. Más ganadores distintos, menos "siempre gana el mismo".</span>
            </div>
            <span class="switch"><input type="checkbox" id="cfg-oneper"></span>
          </label>
          <label class="setting">
            <div class="setting-txt">
              <b data-i18n="cfg.burnT">Quemar la cartela ganadora</b>
              <span data-i18n="cfg.burnD">El número que salió no vuelve al bombo en el mismo sorteo. Desactívalo sólo si quieres que un mismo número pueda ganar dos premios.</span>
            </div>
            <span class="switch"><input type="checkbox" id="cfg-burn"></span>
          </label>
          <label class="setting">
            <div class="setting-txt">
              <b data-i18n="cfg.dramaT">Duelo final</b>
              <span data-i18n="cfg.dramaD">La rueda revela los dígitos en un orden que deja el desempate para el último: hasta el final quedan al menos dos jugadores con chance. No cambia quién gana, sólo la emoción.</span>
            </div>
            <span class="switch"><input type="checkbox" id="cfg-drama"></span>
          </label>
        </div>
      </section>

      <section class="panel">
        <div class="panel-head"><h3 data-i18n="cfg.format">Formato del número</h3></div>
        <div class="settings">
          <div class="setting">
            <div class="setting-txt">
              <b data-i18n="cfg.digitsT">Cantidad de dígitos</b>
              <span data-i18n="cfg.digitsD">Cuántos dígitos tiene cada número de la suerte. Más dígitos, más combinaciones y una revelación más larga en la live. Vale para los números nuevos; los ya emitidos no cambian.</span>
            </div>
            <div class="seg" role="radiogroup" id="cfg-digits">
              <label><input type="radio" name="cfg-digits" value="4"><span>4</span></label>
              <label><input type="radio" name="cfg-digits" value="5"><span>5</span></label>
              <label><input type="radio" name="cfg-digits" value="6"><span>6</span></label>
            </div>
          </div>
          <div class="setting">
            <div class="setting-txt">
              <b data-i18n="cfg.orderT">Orden de revelación</b>
              <span data-i18n="cfg.orderD">De derecha a izquierda es el estilo clásico de la Telesena: el último dígito que abre es el primero del número. De izquierda a derecha se lee como se escribe.</span>
            </div>
            <div class="seg" role="radiogroup" id="cfg-order">
              <label><input type="radio" name="cfg-order" value="rtl"><span data-i18n="cfg.rtlShort">← Derecha a izquierda</span></label>
              <label><input type="radio" name="cfg-order" value="ltr"><span data-i18n="cfg.ltrShort">Izquierda a derecha →</span></label>
            </div>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="panel-head"><h3 data-i18n="cfg.studio">Estudio en vivo</h3></div>
        <div class="settings">
          <label class="setting">
            <div class="setting-txt">
              <b data-i18n="cfg.soundT">Sonido de estudio</b>
              <span data-i18n="cfg.soundD">Tambor, campanas y aplausos durante la revelación. Apágalo si la transmisión ya tiene su propia trilla o si el anfitrión prefiere narrar en silencio.</span>
            </div>
            <span class="switch"><input type="checkbox" id="cfg-sound"></span>
          </label>
        </div>
      </section>
    </div>

    <aside class="span-5 stack">
      <section class="panel cfg-preview">
        <div class="panel-head"><h3 data-i18n="cfg.previewT">Así se ve un número</h3></div>
        <p class="hint" data-i18n="cfg.previewD">Ejemplo con la configuración actual: los dígitos abren en este orden en la live.</p>
        <div class="cfg-sample" id="cfg-sample"></div>
        <div class="cfg-legend">
          <span class="cfg-dot"></span><span data-i18n="cfg.previewOrder">El número indica en qué paso se revela cada dígito.</span>
        </div>
        <dl class="cfg-facts">
          <div><dt data-i18n="cfg.factCombos">Combinaciones posibles</dt><dd id="cfg-combos">—</dd></div>
          <div><dt data-i18n="cfg.factSteps">Pasos de revelación</dt><dd id="cfg-steps">—</dd></div>
          <div><dt data-i18n="cfg.factDuel">Duelo final</dt><dd id="cfg-duel">—</dd></div>
        </dl>
      </section>

      <section class="panel">
        <div class="panel-head"><h3 data-i18n="cfg.whereT">Dónde se guardan</h3></div>
        <p class="hint" data-i18n="cfg.whereD">Estos ajustes se guardan al instante y los usa el estudio en vivo. Las reglas de emisión de números (cuánto vale cada depósito o apuesta) se configuran en Misiones, y los premios dentro de cada sorteo.</p>
      </section>
    </aside>
  </div>
</div>`,

  mount() {
    bindBool('#cfg-oneper', 'onePerLead');
    bindBool('#cfg-burn', 'burnCartela');
    bindBool('#cfg-sound', 'sound');
    bindBool('#cfg-drama', 'dramaticFinal');
    q('#cfg-digits').addEventListener('change', () => { getState().config.digits = parseInt(checked('cfg-digits'), 10); save(); });
    q('#cfg-order').addEventListener('change', () => { getState().config.order = checked('cfg-order') as DrawConfig['order']; save(); });
  },

  render() {
    const c = getState().config;
    q<HTMLInputElement>('#cfg-oneper').checked = c.onePerLead;
    q<HTMLInputElement>('#cfg-burn').checked   = c.burnCartela;
    q<HTMLInputElement>('#cfg-sound').checked  = c.sound;
    q<HTMLInputElement>('#cfg-drama').checked  = c.dramaticFinal !== false;
    setRadio('cfg-digits', String(c.digits));
    setRadio('cfg-order', c.order);
    paintPreview(c);
  }
};

const SAMPLE = '8412975';

/** Prévia: dígitos do número de exemplo com o passo em que cada um abre. */
function paintPreview(c: DrawConfig): void {
  const digits = SAMPLE.slice(0, c.digits).split('');
  const steps = digits.map((_, i) => (c.order === 'rtl' ? digits.length - i : i + 1));
  q('#cfg-sample').innerHTML = digits.map((d, i) =>
    `<div class="cfg-digit" style="--delay:${(steps[i]! - 1) * 0.12}s"><b>${d}</b><small>${steps[i]}º</small></div>`
  ).join('');
  q('#cfg-combos').textContent = Math.pow(10, c.digits).toLocaleString('es-CL');
  q('#cfg-steps').textContent = String(c.digits);
  q('#cfg-duel').textContent = c.dramaticFinal !== false ? t('cfg.on') : t('cfg.off');
  q('#cfg-duel').className = c.dramaticFinal !== false ? 'on' : '';
}

function save(): void {
  commit();
  toast(t('msg.saved'));
}

function checked(name: string): string {
  return q<HTMLInputElement>(`input[name=${name}]:checked`).value;
}

function setRadio(name: string, value: string): void {
  qa<HTMLInputElement>(`input[name=${name}]`).forEach(r => { r.checked = r.value === value; });
}

function bindBool(sel: string, key: 'onePerLead' | 'burnCartela' | 'sound' | 'dramaticFinal'): void {
  q<HTMLInputElement>(sel).onchange = () => {
    getState().config[key] = q<HTMLInputElement>(sel).checked;
    save();
  };
}
